import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import { CheckCircle } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');
  const checkoutId = searchParams.get('checkout_id');
  const transactionId = searchParams.get('transaction_id');
  const invoiceId = searchParams.get('invoice_id');

  useEffect(() => {
    // Verify the payment session
    // Accept either Stripe session_id or SumUp checkout_id/transaction_id
    if (sessionId || checkoutId || transactionId || invoiceId) {
      // Payment successful - we have some payment identifier
      setLoading(false);
    } else {
      // No payment identifier - redirect to dashboard
      navigate('/dashboard');
    }
  }, [sessionId, checkoutId, transactionId, invoiceId, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Confirming payment...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-900">Payment Successful!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <p className="text-green-900 mb-2">
                <strong>Your payment has been processed successfully.</strong>
              </p>
              <p className="text-green-800 text-sm">
                You'll receive a confirmation email shortly with your payment receipt and transaction details.
              </p>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground">
              {(transactionId || sessionId || checkoutId) && (
                <p>
                  <span className="font-medium">Transaction ID:</span>{' '}
                  <span className="font-mono text-xs">{transactionId || sessionId || checkoutId}</span>
                </p>
              )}
              {invoiceId && (
                <p>
                  <span className="font-medium">Invoice ID:</span>{' '}
                  <span className="font-mono text-xs">{invoiceId}</span>
                </p>
              )}
            </div>

            <div className="space-y-3 pt-4">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full"
                size="lg"
              >
                Return to Dashboard
              </Button>
              <p className="text-xs text-muted-foreground">
                Your invoice has been automatically updated with the payment.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}


